import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import ReactTags from 'react-tag-autocomplete';
import _ from 'underscore';
import api from 'helpers/api';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import { useExtendState } from 'helpers/use_extend_state';
import contactType from 'helpers/contact_type';
import { useOnRouteChange } from 'helpers/use_on_route_change';
import Button from '../../components/button';
import classNames from 'classnames';
import Label from '../../components/label';
import Input from '../../components/input';
import Textarea from '../../components/textarea';
import dateFormatter from 'helpers/date_formatter';
import ErrorMessage from '../../components/error_message';

interface Note {
  subject?: string;
  body?: string;
  tag_list: string[];
}

const AddContactNoteModal = ({ modalInstance, contact }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<'add' | 'list'>('add');
  const [note, extendNote, setNote] = useExtendState<Note>({ tag_list: [] });
  const [notes, setNotes] = useState([]);
  const [totalNotes, setTotalNotes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState<any>({});
  const $rootScope = useAngularService('$rootScope');
  const gym = $rootScope.gym;
  const contactTypeLabel = contact.type || contact.contact_type;

  const url = `/gyms/${gym.id}/${contactType(contactTypeLabel)}/${contact.id}/notes`;

  const close = () => {
    modalInstance.dismiss('cancel');
  };

  useOnRouteChange(() => {
    close();
  });

  const loadNotes = (count = 100) => {
    api.get(url, { count }).then(
      (response) => {
        setNotes(response);
        setTotalNotes(response.meta.total);
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  useEffect(() => {
    loadNotes(0);
  }, []);

  useEffect(() => {
    if (tab === 'list') {
      loadNotes();
    }
  }, [tab]);

  const loadSuggestions = useDebouncedCallback((query: string) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    const url = `/gyms/${gym.id}/tags/notes_all?q=${query}`;
    api.get(url).then((response) => {
      setSuggestions(response.map((name, index) => ({ id: index, name })));
    });
  }, 700);

  const onDelete = (tagIndex) => {
    setNote((data) => ({
      ...data,
      tag_list: data.tag_list.filter((tag, index) => tagIndex !== index),
    }));
  };

  const onAddition = (newTag) => {
    setNote((data) => ({
      ...data,
      tag_list: _.uniq(data.tag_list.concat(newTag.name)),
    }));
  };

  const save = () => {
    setIsLoading(true);
    setErrors({});
    api.post(url, { note }).then(
      () => {
        notification.success('Note added successfully');
        close();
      },
      (response) => {
        setIsLoading(false);
        setErrors(response.data.errors);

        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  return (
    <>
      <div className="drawer__backdrop" onClick={() => close()}></div>
      <div className="drawer">
        <div className="drawer__title drawer__title--with-tabs">{contact.name}</div>
        <button type="button" className="drawer__close" onClick={() => close()}>
          <i className="icon-cross" />
        </button>
        <div className="drawer__tabs">
          <button
            type="button"
            className={classNames('drawer__tabs-item', {
              'drawer__tabs-item--active': tab === 'add',
            })}
            onClick={() => setTab('add')}
          >
            New note
          </button>
          <button
            type="button"
            className={classNames('drawer__tabs-item', {
              'drawer__tabs-item--active': tab === 'list',
            })}
            onClick={() => setTab('list')}
          >
            Recent notes
            <span className="drawer__tabs-item-counter">{totalNotes}</span>
          </button>
        </div>
        {tab === 'add' ? (
          <>
            <div className="drawer__inner margin-top-24">
              <div className="margin-bottom-16">
                <Label>Subject</Label>
                <Input
                  value={note.subject}
                  onChange={(subject) => extendNote({ subject })}
                  placeholder="Optional"
                />
              </div>

              <div className="margin-bottom-16">
                <Label>Tag</Label>
                <ReactTags
                  suggestions={suggestions}
                  onDelete={onDelete}
                  onAddition={onAddition}
                  onInput={loadSuggestions}
                  tags={note.tag_list.map((name) => ({ name }))}
                  placeholderText="Add tag"
                  allowNew
                  addOnBlur
                />
              </div>

              <div className="margin-bottom-16">
                <Label>Body</Label>
                <Textarea value={note.body} onChange={(body) => extendNote({ body })} rows={7} />
                <ErrorMessage error={errors.body} />
              </div>
            </div>
            <div className="drawer__footer">
              <Button onClick={() => close()} size="sm" variant="hollow">
                Cancel
              </Button>
              <Button onClick={() => save()} disabled={isLoading} size="sm" variant="success">
                Save
              </Button>
            </div>
          </>
        ) : (
          <div className="drawer__inner contact-notes">
            {notes.map((note) => (
              <div key={note.id} className="contact-notes__item">
                {note.subject ? <div className="contact-notes__subject">{note.subject}</div> : null}

                <div className="contact-notes__body">{note.body}</div>

                <div className="contact-notes__time">
                  Added by {note.user_name} {dateFormatter(note.created_at)}
                </div>

                {note.tag_list.length > 0 ? (
                  <div>
                    {note.tag_list.map((tag, index) => (
                      <span key={index} className="contact-notes__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

angular.module('shared').value('AddContactNoteModal', AddContactNoteModal);
